import { Navigate } from 'react-router-dom';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import 'intro.js/introjs.css';

import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';

// Components
import Top10Costs from '@/components/shared-charts/top-10-costs';
import CostPerDay from '@/components/shared-charts/cost-per-day-chart';
import CostsByResourceType from '@/screens/reporting/components/costs-by-resource-type';
import { ResourcesCosts } from '@/screens/reporting/components/resources-costs';
import SortableItem from '@/components/sortable-item';
import { LightUserDto } from '@cloudcost/shared-types/users';

// Utils
import { ROUTES, urls } from '@/urls';
import { getToken, getUser } from '@/lib/common';
import { AppCard } from '@/components/app-card/app-card';
import { AppContainer } from '@/components/ui';
import { CSSProperties, JSX, useEffect, useMemo, useState } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import ReportingFilters from './reporting-filters';
import { styled } from '@mui/material/styles';
import { formatDate } from '@/lib/formatters';
import { FilterSubscription } from '@cloudcost/shared-types/reporting';
import { useTimeMachineUtcNow } from '@/hooks/useTimeMachineUtcNow';

// Total Spending chart legend, shown right-aligned in the card header
// (Reporting only — the embedded chart's own legend is suppressed via hideLegend).
function TotalSpendingLegend() {
  const item = (label: string, swatch: CSSProperties) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#5A6472' }}>
      <span style={{ display: 'inline-block', ...swatch }} />
      {label}
    </span>
  );
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 14,
        fontSize: 12,
        fontWeight: 700,
      }}>
      {item('Actual', { width: 14, height: 3, borderRadius: 2, background: '#1C71E3' })}
      {item('Last Month', { width: 14, height: 3, borderRadius: 2, background: '#4E79A7' })}
      {item('Forecast', { width: 14, height: 3, borderRadius: 2, background: '#F28E2C' })}
      {item('Budget', { width: 14, height: 0, borderTop: '2px solid #E23D3D' })}
    </div>
  );
}

const CARD_IDS = [
  'total-spending-card',
  'top-10-systems-card',
  'top-10-user-card',
  'top-10-departments-card',
  'top-10-resources-card',
  'costs-by-resource-type-card',
  'resources-cost-table-card',
];

const LOCAL_STORAGE_ORDER_KEY = 'reporting-card-order';
const DEFAULT_CARD_ORDER = CARD_IDS;

function getReportingDefaultDateTo(now: Date) {
  // Use UTC components to avoid local timezone shifting the intended date/month.
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth(); // 0-based
  const d = now.getUTCDate();

  // If today is the first day of the month, take the last day of previous month
  // because we do not have any data on the first day of the month.
  if (d === 1) {
    return new Date(Date.UTC(y, m, 0));
  }

  return new Date(Date.UTC(y, m, d));
}

function Reporting() {
  const { utcNow } = useTimeMachineUtcNow();
  const fallbackNow = useMemo(() => new Date(), []);
  const now = utcNow ?? fallbackNow;
  const dateTo = useMemo(() => getReportingDefaultDateTo(now), [now]);

  const [filters, setFilters] = useState<any>({
    dateTo: formatDate(dateTo),
    dateToDisplay: dateTo,
    currency: getUser()?.default_currency ?? null,
    cloudAccount: null,
    cloudAccountOwner: null,
    department: null,
    system: null,
    subscription: null,
    owner: null,
    assigned: null,
  });

  const computedFilters = useMemo(() => {
    return Object.entries(filters)
      .filter(([k, v]) => k !== 'dateToDisplay')
      .filter(([key, value]) => value !== null && value !== '')
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
  }, [filters]);

  const [filterUsers, setFilterUsers] = useState<LightUserDto[]>([]);
  const [filterDepartments, setFilterDepartments] = useState([]);
  const [filterSystems, setFilterSystems] = useState([]);
  const [filterProviders, setFilterProviders] = useState([]);
  const [filterCloudAccountOwners, setFilterCloudAccountOwners] = useState([]);
  const [filterSubscriptions, setFilterSubscriptions] = useState<
    FilterSubscription[]
  >([]);
  const [filterCurrencies, setFilterCurrencies] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const [cardOrder, setCardOrder] = useState(() => {
    try {
      const savedOrder = localStorage.getItem(LOCAL_STORAGE_ORDER_KEY);
      if (savedOrder) {
        const parsedOrder = JSON.parse(savedOrder);

        if (
          Array.isArray(parsedOrder) &&
          parsedOrder.length === DEFAULT_CARD_ORDER.length &&
          parsedOrder.every((id) => DEFAULT_CARD_ORDER.includes(id))
        ) {
          return parsedOrder as string[];
        } else {
          console.warn('Invalid saved card order in local storage.');
          return DEFAULT_CARD_ORDER;
        }
      }
    } catch (error) {
      console.error(
        'Error loading saved card order from local storage:',
        error,
      );
    }

    return DEFAULT_CARD_ORDER;
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCardOrder((currentItems) => {
        const oldIndex = currentItems.indexOf(active.id as string);
        const newIndex = currentItems.indexOf(over.id as string);
        const newOrder = arrayMove(currentItems, oldIndex, newIndex);

        try {
          localStorage.setItem(
            LOCAL_STORAGE_ORDER_KEY,
            JSON.stringify(newOrder),
          );
        } catch (error) {
          console.error('Error saving card order to local storage:', error);
        }

        return newOrder;
      });
    }
  }

  const filterData = useMemo(() => {
    return {
      currency: [...filterCurrencies].sort((a: any, b: any) =>
        a.localeCompare(b),
      ),
      owner: [...filterUsers].sort((a: any, b: any) =>
        a.username.localeCompare(b.username),
      ),
      department: [...filterDepartments].sort((a: any, b: any) =>
        a.name.localeCompare(b.name),
      ),
      cloudAccount: [...filterProviders].sort((a: any, b: any) =>
        a.name.localeCompare(b.name),
      ),
      cloudAccountOwner: [...filterCloudAccountOwners].sort((a: any, b: any) =>
        a.username.localeCompare(b.username),
      ),
      system: [...filterSystems].sort((a: any, b: any) =>
        a.name.localeCompare(b.name),
      ),
      subscription: [...filterSubscriptions].sort((a: any, b: any) =>
        a.display_name.localeCompare(b.display_name),
      ),
    };
  }, [
    filterDepartments,
    filterProviders,
    filterCloudAccountOwners,
    filterSystems,
    filterUsers,
    filterSubscriptions,
    filterCurrencies,
  ]);

  const cardComponents: { [key: string]: JSX.Element } = useMemo(
    () => ({
      'total-spending-card': (
        <AppCard
          key="total-spending-card"
          id="total-spending-card"
          className="card50 sortableExpandable"
          title="Total Spending"
          intro="This chart show how much you have spent till now in the current month (the blue line). The Yellow line shows the forecasted cost untill the end of the month. <br /> The dotted lines show your global budget (can be set up by the CFO in the admin center), and how much have you spent in total last month."
          icon={<BarChartOutlinedIcon color="primary" />}
          headerRight={<TotalSpendingLegend />}
          draggable>
          <CostPerDay filters={computedFilters} hideAppCard hideLegend />
        </AppCard>
      ),
      'top-10-departments-card': (
        <AppCard
          key="top-10-departments-card"
          id="top-10-departments-card"
          className="card50 sortableExpandable"
          title="Top 10 Departments Costs"
          intro="This chart show how much you have spent till now in the current month (the blue line). The Yellow line shows the forecasted cost untill the end of the month. <br /> The dotted lines show your global budget (can be set up by the CFO in the admin center), and how much have you spent in total last month."
          icon={<BarChartOutlinedIcon color="primary" />}
          draggable>
          <Top10Costs
            url={urls.DevOps.top10departments}
            filters={computedFilters}
          />
        </AppCard>
      ),
      'top-10-systems-card': (
        <AppCard
          key="top-10-systems-card"
          id="top-10-systems-card"
          className="card50 sortableExpandable"
          title="Top 10 Systems Costs"
          intro="This chart show how much you have spent till now in the current month (the blue line). The Yellow line shows the forecasted cost untill the end of the month. <br /> The dotted lines show your global budget (can be set up by the CFO in the admin center), and how much have you spent in total last month."
          icon={<BarChartOutlinedIcon color="primary" />}
          draggable>
          <Top10Costs
            url={urls.DevOps.top10systems}
            filters={computedFilters}
          />
        </AppCard>
      ),
      'top-10-user-card': (
        <AppCard
          key="top-10-user-card"
          id="top-10-user-card"
          className="card50 sortableExpandable"
          title="Top 10 Costs by Resource Owner"
          intro="This chart show how much you have spent till now in the current month (the blue line). The Yellow line shows the forecasted cost untill the end of the month. <br /> The dotted lines show your global budget (can be set up by the CFO in the admin center), and how much have you spent in total last month."
          icon={<BarChartOutlinedIcon color="primary" />}
          draggable>
          <Top10Costs
            url={urls.DevOps.top10users}
            filters={computedFilters}
          />
        </AppCard>
      ),
      'top-10-resources-card': (
        <AppCard
          key="top-10-resources-card"
          id="top-10-resources-card"
          className="card50 sortableExpandable"
          title="Top 10 Resources Costs"
          intro="This chart show how much you have spent till now in the current month (the blue line). The Yellow line shows the forecasted cost untill the end of the month. <br /> The dotted lines show your global budget (can be set up by the CFO in the admin center), and how much have you spent in total last month."
          icon={<BarChartOutlinedIcon color="primary" />}
          draggable>
          <Top10Costs
            url={urls.DevOps.top10resources}
            filters={computedFilters}
          />
        </AppCard>
      ),
      'costs-by-resource-type-card': (
        <AppCard
          key="costs-by-resource-type-card"
          id="costs-by-resource-type-card"
          className="card50 sortableExpandable"
          title="Costs by Resource Type"
          intro="This chart show how much you have spent till now in the current month (the blue line). The Yellow line shows the forecasted cost untill the end of the month. <br /> The dotted lines show your global budget (can be set up by the CFO in the admin center), and how much have you spent in total last month."
          icon={<BarChartOutlinedIcon color="primary" />}
          draggable>
          <CostsByResourceType filters={computedFilters} />
        </AppCard>
      ),
      'resources-cost-table-card': (
        <AppCard
          key="resources-cost-table-card"
          id="resources-cost-table-card"
          className="card100"
          title="Costs by Resource"
          intro="This table shows how much you are spending on all exisitng resources. By utilizing the filtering options you can also present a breakdown by types, different cloud providers, owners or even systems."
          icon={<BarChartOutlinedIcon color="primary" />}
          draggable // Make AppCard draggable
        >
          <ResourcesCosts filters={computedFilters} />
        </AppCard>
      ),
    }),
    [computedFilters],
  );

  useEffect(() => {
    setLoading(true);

    fetchWrapper
      .get(urls.DevOps.getFilterUsers)
      .then((data) => {
        setFilterUsers(data);
      })
      .catch((error) => console.error('Error fetching users:', error));

    fetchWrapper
      .get(urls.DevOps.getFilterCloudAccounts)
      .then((data) => {
        setFilterProviders(data);
      })
      .catch((error) => console.error('Error fetching cloud accounts:', error));

    fetchWrapper
      .get(urls.DevOps.getFilterCloudAccountOwners)
      .then((data) => {
        setFilterCloudAccountOwners(data);
      })
      .catch((error) => console.error('Error fetching cloud account owners:', error));

    fetchWrapper
      .get(urls.DevOps.getFilterDepartments)
      .then((data) => {
        setFilterDepartments(data);
      })
      .catch((error) => console.error('Error fetching departments:', error));

    fetchWrapper
      .get(urls.DevOps.getFilterSystems)
      .then((data) => {
        setFilterSystems(data);
      })
      .catch((error) => console.error('Error fetching systems:', error));

    fetchWrapper
      .get(urls.DevOps.currencies)
      .then((data) => {
        setFilterCurrencies(
          (data || [])
            .map((item: any) => item.currency)
            .filter(Boolean),
        );
      })
      .catch((error) => console.error('Error fetching currencies:', error));

    fetchWrapper
      .get(urls.DevOps.getFilterSubscriptions)
      .then((data) => {
        setFilterSubscriptions(data);
      })
      .catch((error) =>
        console.error('Error fetching resource costs for filters:', error),
      )
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // If timemachine date arrives after initial render, align the filter defaults with it.
  useEffect(() => {
    if (!utcNow) return;
    const tmDateTo = getReportingDefaultDateTo(utcNow);
    setFilters((prev: any) => ({
      ...prev,
      dateTo: formatDate(tmDateTo),
      dateToDisplay: tmDateTo,
    }));
  }, [utcNow]);

  if (!getToken()) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return (
    <AppContainer sx={{ gap: 0 }}>
      <ReportingFilters
        filters={filters}
        setFilters={setFilters}
        filterData={filterData}
        loading={loading}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}>
        <StyledCardsContainer>
          <SortableContext items={cardOrder} strategy={rectSortingStrategy}>
            {cardOrder.map((cardId) => (
              <SortableItem
                key={cardId}
                id={cardId}
                style={{
                  flexBasis:
                    cardComponents[cardId].props.className.includes('card100')
                      ? '100%'
                      : 'calc(50% - 0.5rem)',
                  maxWidth:
                    cardComponents[cardId].props.className.includes('card100')
                      ? '100%'
                      : 'calc(50% - 0.5rem)',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                {cardComponents[cardId]}
              </SortableItem>
            ))}
          </SortableContext>
        </StyledCardsContainer>
      </DndContext>
    </AppContainer>
  );
}

export default Reporting;

const StyledCardsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '1rem',
  width: '100%',
  boxSizing: 'border-box',
}));
